import React from 'react';

// Magic Moments Logo - Wand tapping play button with sparkles
const Logo = ({ className = "w-10 h-10", animated = false }) => {
  // Generate unique IDs for each logo instance to avoid conflicts
  const uniqueId = React.useId ? React.useId() : Math.random().toString(36).substr(2, 9);
  const bgGradientId = `bgGradientLogo-${uniqueId}`;
  const glowId = `glowLogo-${uniqueId}`;
  
  return (
    <svg className={className} viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id={bgGradientId} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" style={{stopColor: '#F59E0B', stopOpacity: 1}} />
          <stop offset="50%" style={{stopColor: '#F97316', stopOpacity: 1}} />
          <stop offset="100%" style={{stopColor: '#FBBF24', stopOpacity: 1}} />
        </linearGradient>
        {/* Glow filter and metallic gradients for realistic star */}
        <filter id={glowId} x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
          <feMerge>
            <feMergeNode in="coloredBlur"/>
            <feMergeNode in="SourceGraphic"/>
          </feMerge>
        </filter>
        
        {/* Rich textured gold gradient - Option D */}
        <radialGradient id={`goldGradient-${uniqueId}`} cx="35%" cy="35%">
          <stop offset="0%" stopColor="#FFF4D6" stopOpacity="1"/>
          <stop offset="20%" stopColor="#FDE68A" stopOpacity="1"/>
          <stop offset="45%" stopColor="#FBBF24" stopOpacity="1"/>
          <stop offset="70%" stopColor="#F59E0B" stopOpacity="1"/>
          <stop offset="90%" stopColor="#D97706" stopOpacity="1"/>
          <stop offset="100%" stopColor="#B45309" stopOpacity="1"/>
        </radialGradient>
        
        {/* Shine overlay for textured effect */}
        <radialGradient id={`goldShine-${uniqueId}`} cx="65%" cy="30%">
          <stop offset="0%" stopColor="#FFFEF5" stopOpacity="0.9"/>
          <stop offset="40%" stopColor="#FEF3C7" stopOpacity="0.5"/>
          <stop offset="80%" stopColor="#F59E0B" stopOpacity="0.2"/>
          <stop offset="100%" stopColor="#D97706" stopOpacity="0"/>
        </radialGradient>
        
        {/* Shadow for depth */}
        <radialGradient id={`goldShadow-${uniqueId}`} cx="60%" cy="65%">
          <stop offset="0%" stopColor="#78350F" stopOpacity="0.6"/>
          <stop offset="100%" stopColor="#78350F" stopOpacity="0.6"/>
        </radialGradient>
      </defs>
      
      {/* Orange gradient background box */}
      <rect x="18" y="18" width="84" height="84" rx="18" fill={`url(#${bgGradientId})`}/>
      
      {/* Play button (white triangle) - PROPERLY CENTERED */}
      <path d="M50 42 L50 78 L80 60 Z" fill="white"/>
      
      {/* LARGER TWINKLE SPARKLES - Slightly random spacing for natural look */}
      {/* 4-pointed star sparkles with organic distribution */}
      
      {/* Top area - slightly offset */}
      <g className={animated ? "sparkle-twinkle" : ""} style={{animationDelay: '0s'}}>
        <path d="M27 26 L30 31 L27 36 L24 31 Z" fill="white" opacity="0.9" filter={`url(#${glowId})`}/>
        <path d="M22 31 L27 28 L32 31 L27 34 Z" fill="white" opacity="0.9" filter={`url(#${glowId})`}/>
      </g>
      <g className={animated ? "sparkle-twinkle" : ""} style={{animationDelay: '0.1s'}}>
        <path d="M52 23 L55 28 L52 33 L49 28 Z" fill="#FDE68A" opacity="0.9" filter={`url(#${glowId})`}/>
        <path d="M47 28 L52 25 L57 28 L52 31 Z" fill="#FDE68A" opacity="0.9" filter={`url(#${glowId})`}/>
      </g>
      <g className={animated ? "sparkle-twinkle" : ""} style={{animationDelay: '0.2s'}}>
        <path d="M79 27 L82 32 L79 37 L76 32 Z" fill="white" opacity="0.9" filter={`url(#${glowId})`}/>
        <path d="M74 32 L79 29 L84 32 L79 35 Z" fill="white" opacity="0.9" filter={`url(#${glowId})`}/>
      </g>
      
      {/* Upper middle - organic spacing */}
      <g className={animated ? "sparkle-twinkle" : ""} style={{animationDelay: '0.3s'}}>
        <path d="M33 44 L36 49 L33 54 L30 49 Z" fill="#FDE68A" opacity="0.9" filter={`url(#${glowId})`}/>
        <path d="M28 49 L33 46 L38 49 L33 52 Z" fill="#FDE68A" opacity="0.9" filter={`url(#${glowId})`}/>
      </g>
      <g className={animated ? "sparkle-twinkle" : ""} style={{animationDelay: '0.4s'}}>
        <path d="M68 42 L71 47 L68 52 L65 47 Z" fill="white" opacity="0.9" filter={`url(#${glowId})`}/>
        <path d="M63 47 L68 44 L73 47 L68 50 Z" fill="white" opacity="0.9" filter={`url(#${glowId})`}/>
      </g>
      <g className={animated ? "sparkle-twinkle" : ""} style={{animationDelay: '0.5s'}}>
        <path d="M94 43 L97 48 L94 53 L91 48 Z" fill="#FDE68A" opacity="0.9" filter={`url(#${glowId})`}/>
        <path d="M89 48 L94 45 L99 48 L94 51 Z" fill="#FDE68A" opacity="0.9" filter={`url(#${glowId})`}/>
      </g>
      
      {/* Middle area - varied positions */}
      <g className={animated ? "sparkle-twinkle" : ""} style={{animationDelay: '0.6s'}}>
        <path d="M24 64 L27 69 L24 74 L21 69 Z" fill="white" opacity="0.9" filter={`url(#${glowId})`}/>
        <path d="M19 69 L24 66 L29 69 L24 72 Z" fill="white" opacity="0.9" filter={`url(#${glowId})`}/>
      </g>
      <g className={animated ? "sparkle-twinkle" : ""} style={{animationDelay: '0.7s'}}>
        <path d="M46 62 L49 67 L46 72 L43 67 Z" fill="#FDE68A" opacity="0.9" filter={`url(#${glowId})`}/>
        <path d="M41 67 L46 64 L51 67 L46 70 Z" fill="#FDE68A" opacity="0.9" filter={`url(#${glowId})`}/>
      </g>
      
      {/* Near star - natural placement */}
      <g className={animated ? "sparkle-twinkle" : ""} style={{animationDelay: '0.8s'}}>
        <path d="M71 77 L74 82 L71 87 L68 82 Z" fill="white" opacity="0.9" filter={`url(#${glowId})`}/>
        <path d="M66 82 L71 79 L76 82 L71 85 Z" fill="white" opacity="0.9" filter={`url(#${glowId})`}/>
      </g>
      <g className={animated ? "sparkle-twinkle" : ""} style={{animationDelay: '0.9s'}}>
        <path d="M97 79 L100 84 L97 89 L94 84 Z" fill="#FDE68A" opacity="0.9" filter={`url(#${glowId})`}/>
        <path d="M92 84 L97 81 L102 84 L97 87 Z" fill="#FDE68A" opacity="0.9" filter={`url(#${glowId})`}/>
      </g>
      
      {/* Bottom area - organic distribution */}
      <g className={animated ? "sparkle-twinkle" : ""} style={{animationDelay: '1.0s'}}>
        <path d="M29 87 L32 92 L29 97 L26 92 Z" fill="#FDE68A" opacity="0.9" filter={`url(#${glowId})`}/>
        <path d="M24 92 L29 89 L34 92 L29 95 Z" fill="#FDE68A" opacity="0.9" filter={`url(#${glowId})`}/>
      </g>
      <g className={animated ? "sparkle-twinkle" : ""} style={{animationDelay: '1.1s'}}>
        <path d="M57 89 L60 94 L57 99 L54 94 Z" fill="white" opacity="0.9" filter={`url(#${glowId})`}/>
        <path d="M52 94 L57 91 L62 94 L57 97 Z" fill="white" opacity="0.9" filter={`url(#${glowId})`}/>
      </g>
      <g className={animated ? "sparkle-twinkle" : ""} style={{animationDelay: '1.2s'}}>
        <path d="M77 96 L80 101 L77 106 L74 101 Z" fill="white" opacity="0.9" filter={`url(#${glowId})`}/>
        <path d="M72 101 L77 98 L82 101 L77 104 Z" fill="white" opacity="0.9" filter={`url(#${glowId})`}/>
      </g>
      
      {/* STAR GLOW - Drawn first */}
      {/* Subtle outer glow */}
      <circle cx="88" cy="88" r="17" fill="white" opacity="0.5" filter={`url(#${glowId})`}/>
      <circle cx="88" cy="88" r="14" fill="#F59E0B" opacity="0.7" filter={`url(#${glowId})`}/>
      
      {/* WAND SHAFT - Drawn AFTER glow so it's visible on top */}
      {/* Black handle going diagonally */}
      <line x1="115" y1="115" x2="88" y2="88" stroke="#1F2937" strokeWidth="8" strokeLinecap="round"/>
      
      {/* Simple rounded cap at handle bottom */}
      <circle cx="115" cy="115" r="4.5" fill="#1F2937"/>
      
      {/* 5-pointed star with subtle rounded tips - RICH TEXTURED GOLD (Option D) */}
      <g transform="translate(-3, 3) rotate(27 88 88)">
        {/* Dark base shadow for depth */}
        <path d="M88,66.5 L90.9,77.4 L101.7,79.1 L93.9,86 L96.3,96.8 L88,91 L79.7,96.8 L82.1,86 L74.3,79.1 L85.1,77.4 Z" 
              fill={`url(#goldShadow-${uniqueId})`}
              transform="translate(1,1)"/>
        
        {/* Rich gold base with subtle rounded tips */}
        <path d="M88,66 L90.8,77.3 L101.5,79 L93.8,85.8 L96.2,96.5 L88,90.8 L79.8,96.5 L82.2,85.8 L74.5,79 L85.2,77.3 Z" 
              fill={`url(#goldGradient-${uniqueId})`}
              stroke="#92400E" 
              strokeWidth="1.5"
              strokeLinejoin="round"/>
        
        {/* Shine layer for textured metallic effect */}
        <path d="M88,68 L90.2,77.8 L99.5,79.3 L92.8,85 L95,94 L88,89.5 L81,94 L83.2,85 L76.5,79.3 L85.8,77.8 Z" 
              fill={`url(#goldShine-${uniqueId})`}/>
        
        {/* Highlight band for dimension */}
        <path d="M88,70 L89.5,77 L95.5,78 L91.5,82.5 L93,90 L88,86 L83,90 L84.5,82.5 L80.5,78 L86.5,77 Z" 
              fill="white"
              opacity="0.35"/>
        
        {/* Accent gleams for sparkle */}
        <circle cx="85" cy="73" r="1.5" fill="white" opacity="0.85"/>
        <circle cx="90" cy="82" r="1" fill="white" opacity="0.7"/>
      </g>
      
      {/* Animation styles */}
      {animated && (
        <style>{`
          @keyframes sparkle {
            0%, 100% { opacity: 0.5; }
            50% { opacity: 1; }
          }
          .sparkle-twinkle {
            animation: sparkle 1.5s ease-in-out infinite;
          }
        `}</style>
      )}
    </svg>
  );
};

export default Logo;
